/**
 * ListeningCatalogue — Student-facing list of 54 IELTS Listening practice
 * tests. Audio + question booklet are deep-linked to source (ieltsfever.org)
 * with full attribution; our app provides the timer, answer-input UI, and
 * auto-grading from our scraped key.
 *
 * Route: /app/listening-catalogue
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Search, Headphones, FileText, ChevronRight, Loader2 } from "lucide-react";

export default function ListeningCatalogue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/listening-catalogue", { params: { limit: 100 } });
        setItems(Array.isArray(r.data) ? r.data : []);
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = items.filter((t) => {
    const s = search.trim().toLowerCase();
    if (!s) return true;
    return [
      `test ${t.test_number}`,
      t.title,
      ...(t.sections || []).map((sec) => sec.topic),
    ].filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
  });

  return (
    <div className="min-h-screen px-6 py-8 max-w-7xl mx-auto" data-testid="listening-catalogue">
      <div className="mb-2">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase opacity-60">// IELTS LISTENING · PRACTICE TESTS</div>
        <h1 className="text-3xl md:text-4xl font-bold mt-1">Listening Catalogue · {items.length} tests</h1>
        <p className="text-sm opacity-70 mt-2 max-w-3xl">
          Practise with 50+ full-length IELTS Listening tests. Each test is 4 sections (40 questions ·
          ~30 min audio). Play the audio in our timer, type your answers, and we'll grade them
          instantly using the British Council band conversion.
        </p>
        <p className="text-[11px] mt-2 font-mono opacity-60">
          Audio &amp; question booklets sourced from <a href="https://ieltsfever.org/ielts-listening-test-sample-papers/" target="_blank" rel="noreferrer" className="underline">ieltsfever.org</a> ·
          deep-linked under fair-use for educational practice (not re-hosted).
        </p>
      </div>

      <div className="flex items-center gap-2 mt-5 mb-4 px-3 py-2 rounded-sm border max-w-md" style={{ borderColor: "rgba(0,0,0,0.15)" }}>
        <Search className="w-4 h-4 opacity-60" />
        <input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Search by test number or section topic"
               data-testid="catalogue-search"
               className="outline-none bg-transparent flex-1 text-sm" />
      </div>

      {loading && (
        <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="py-20 text-center opacity-60" data-testid="catalogue-empty">No tests match your search.</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="catalogue-grid">
        {filtered.map((t) => (
          <Link key={t.test_number} to={`/app/listening-catalogue/${t.test_number}`}
                data-testid={`catalogue-card-${t.test_number}`}
                className="block p-4 rounded-sm border hover:border-[#F77F00] hover:shadow-md transition-all bg-white">
            <div className="flex items-start justify-between gap-2">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase opacity-60">
                TEST {String(t.test_number).padStart(2, "0")}
              </div>
              <div className="font-mono text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full"
                   style={{ background: "rgba(247,127,0,0.12)", color: "#B45309" }}>
                {t.answer_count}/40 keyed
              </div>
            </div>
            <div className="mt-2 font-display font-semibold text-[#003049] leading-snug">
              {t.title}
            </div>
            <div className="mt-3 space-y-1.5 text-xs">
              {(t.sections || []).map((sec) => (
                <div key={sec.section_no} className="flex gap-2">
                  <span className="font-mono opacity-50 w-12 flex-shrink-0">S{sec.section_no} · Q{sec.q_range?.[0]}-{sec.q_range?.[1]}</span>
                  <span className="opacity-80 truncate">{sec.topic}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between pt-3 border-t" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
              <div className="flex items-center gap-3 text-xs opacity-60">
                <span className="inline-flex items-center gap-1"><Headphones className="w-3.5 h-3.5" /> Audio</span>
                <span className="inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> PDF</span>
              </div>
              <ChevronRight className="w-4 h-4 opacity-50" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
